import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import StickyProductBar from './components/StickyProductBar';
import ProductHero from './components/ProductHero';
import PharmacyMap from './components/PharmacyMap';
import ProductTabs from './components/ProductTabs';
import ProductFaq from './components/ProductFaq';
import RelatedProducts from './components/RelatedProducts';
import SimilarProducts from './components/SimilarProducts';
import ArticleLinks from './components/ArticleLinks';
import SeoBlocks from './components/SeoBlocks';
import RecentlyViewed from './components/RecentlyViewed';
import { productDetail } from '../../mocks/product';
import { useCart } from '@/hooks/useCart';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const ProductPage = () => {
  const product = productDetail;
  const { addItem, openCart } = useCart();
  const { addView } = useRecentlyViewed();

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.images,
    description: product.description,
    sku: product.sku ?? `NP-${product.id}`,
    gtin13: product.sku ?? `NP-${product.id}`,
    brand: {
      '@type': 'Brand',
      name: product.brand ?? 'NeonPharma',
    },
    manufacturer: {
      '@type': 'Organization',
      name: product.brand ?? 'NeonPharma Labs',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'DE',
      },
    },
    category: 'Нейропротекторы / БАД',
    audience: {
      '@type': 'Audience',
      audienceType: 'Взрослые 18+',
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.id}`,
      priceCurrency: 'RUB',
      price: product.price,
      priceValidUntil: '2026-12-31',
      availability: product.inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'NeonPharma',
        url: SITE_URL,
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'RUB',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: '0',
            maxValue: '1',
            unitCode: 'HUR',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: '1',
            maxValue: '4',
            unitCode: 'HUR',
          },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      bestRating: 5,
      worstRating: 1,
      reviewCount: product.reviewsCount ?? product.reviews ?? 1,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Каталог лекарств', item: `${SITE_URL}/catalog` },
        { '@type': 'ListItem', position: 3, name: 'Нейропротекторы', item: `${SITE_URL}/catalog?category=Нейропротекторы` },
        { '@type': 'ListItem', position: 4, name: product.name, item: `${SITE_URL}/product/${product.id}` },
      ],
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const timer = setTimeout(() => {
      addView({
        id: product.id,
        name: product.name,
        price: product.price,
        oldPrice: product.oldPrice,
        image: product.images[0],
        color: product.color,
        category: 'Нейропротекторы',
        rating: product.rating,
        discount: product.discount,
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [product, addView]);

  const handleBuy = () => {
    const section = document.getElementById('product-hero');
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice,
      image: product.images[0],
      color: product.color,
    });
    openCart();
  };

  return (
    <div style={{ background: '#050508', minHeight: '100vh' }}>
      <Navbar />
      <SeoHead
        title={`${product.name} — купить в интернет-аптеке NeonPharma | Цена ${product.price}₽`}
        description={`${product.name} ${product.subtitle ? `(${product.subtitle})` : ''} — цена ${product.price}₽ в интернет-аптеке NeonPharma. ${product.description ?? ''} Доставка за 2 часа по Москве, самовывоз сегодня. Без рецепта. Оригинальный товар.`}
        keywords={`${product.name}, ${product.brand}, купить ${product.name}, цена ${product.name}, ${product.name} аптека, ${product.name} доставка, нейропротекторы купить, витамины для мозга`}
        canonical={`/product/${product.id}`}
        ogType="product"
        ogImage={product.images?.[0]}
        jsonLd={productJsonLd}
      />
      {/* Back to catalog */}
      <div style={{ background: '#050508', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-11 flex items-center">
          <a
            href="/"
            className="flex items-center gap-2 font-mono text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Вернуться в каталог
          </a>
        </div>
      </div>

      {/* Sticky bar */}
      <StickyProductBar
        name={product.name}
        price={product.price}
        oldPrice={product.oldPrice}
        color={product.color}
        inStock={product.inStock}
        onBuy={handleBuy}
        onCart={handleCart}
      />

      {/* Main hero */}
      <div id="product-hero">
        <ProductHero />
      </div>

      {/* Tabs: instructions, characteristics, reviews, analogues */}
      <ProductTabs />

      {/* FAQ accordion */}
      <ProductFaq />

      {/* Pharmacy map — before bought together */}
      <PharmacyMap />

      {/* Bought together */}
      <RelatedProducts />

      {/* Similar products */}
      <SimilarProducts />

      {/* Recently viewed */}
      <RecentlyViewed currentProductId={product.id} />

      {/* Article links */}
      <ArticleLinks />

      {/* SEO blocks + FAQ */}
      <SeoBlocks />

      {/* SEO blocks + FAQ */}
      <Footer />
    </div>
  );
};

export default ProductPage;
